// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { type FC, useRef, useState, useEffect } from 'react';
import styles from './period-picker.module.css';
import { usePeriodPicker, YTD_VALUE } from './use-period-picker';
import { DatePicker } from '../date-picker';
import { useTranslation } from 'react-i18next';
import { IconButton } from '../icon-button';

export type HistValue =
  | 'YTD'
  | '1D'
  | '1W'
  | '1M'
  | '3M'
  | '6M'
  | '1Y'
  | '3Y'
  | '5Y'
  | 'MAX'
  | '-';

export const HistValues = {
  '1D': 1,
  '1W': 7,
  '1M': 30,
  '3M': 85,
  '6M': 182,
  '1Y': 365,
  '3Y': 1_095,
  '5Y': 1_825,
  MAX: 10_000,
  '-': 0,
} as const;

export interface PeriodPickerProps {
  readonly defaultValue?: HistValue;
  readonly onValueChange?: (value: number) => void;
  readonly onDateChange?: (startDate: Date, endDate: Date) => void;
  readonly showDatePicker?: boolean;
  readonly availableOptions?: HistValue[] | 'all';
  readonly disabled?: boolean;
}

export const HIST_VALUES = new Map<number, HistValue>([
  [YTD_VALUE, 'YTD'],
  [1, '1D'],
  [7, '1W'],
  [30, '1M'],
  [85, '3M'],
  [182, '6M'],
  [365, '1Y'],
  [1_095, '3Y'],
  [1_825, '5Y'],
  [10_000, 'MAX'],
  [0, '-'],
] as const);

export const PeriodPicker: FC<PeriodPickerProps> = props => {
  const { t } = useTranslation('glossary');
  const {
    periods,
    onItemClick,
    value: currentValue,
    enablePicker,
  } = usePeriodPicker(props);

  const periodsListRef = useRef<HTMLUListElement>(null);
  const [isOverflowing, setIsOverflowing] = useState({ left: false, right: false });

  const updateOverflowState = () => {
    if (periodsListRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = periodsListRef.current;
      setIsOverflowing({
        left: scrollLeft > 0,
        right: scrollLeft + clientWidth < scrollWidth,
      });
    }
  };

  const scroll = (direction: 'left' | 'right') => {
    if (periodsListRef.current) {
      const scrollAmount = direction === 'left' ? -200 : 200;
      periodsListRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    updateOverflowState();
    const handleResize = () => updateOverflowState();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="flex shrink-0 items-center justify-center">
      <div className={styles.scrollableContainer}>
        <IconButton
          variant="icon"
          icon="ArrowLeft"
          className={`!pr-2.5 ${styles.scrollButton}`}
          size="xl"
          onClick={() => scroll('left')}
          data-testid="period-scroll-left"
          disabled={props.disabled || !isOverflowing.left}
        />
        <ul
          ref={periodsListRef}
          className={styles.container}
          data-testid="period-picker"
          data-scrollable="true"
          role="radiogroup"
          aria-disabled={props.disabled}
          onScroll={updateOverflowState}
        >
          {Array.from(periods, ({ label, value }) => (
            <li key={label} aria-hidden={label === '-'} hidden={label === '-'}>
              <button
                type="button"
                aria-current={value === currentValue}
                value={value}
                onClick={onItemClick}
                className={styles.button}
                data-testid={`period-button-${label}`}
                disabled={props.disabled}
              >
                {t(`glossary.period-${label}`, label)}
              </button>
            </li>
          ))}
        </ul>
        <IconButton
          variant="icon"
          icon="ArrowRight"
          className={`!pl-2.5 ${styles.scrollButton}`}
          size="xl"
          onClick={() => scroll('right')}
          data-testid="period-scroll-right"
          disabled={props.disabled || !isOverflowing.right}
        />
      </div>
      {enablePicker && (
        <>
          <hr className="h-8 w-0.5 border-l-2 border-section" />
          <DatePicker
            onDateChange={props.onDateChange}
            onClick={onItemClick}
            aria-selected={currentValue === 0}
            disabled={props.disabled}
          />
        </>
      )}
    </div>
  );
};
